/** Short page banners + comprehensive section/tab help texts. */

export type PageGuideKey =
  | 'overview'
  | 'index'
  | 'inspect'
  | 'urls'
  | 'changes'
  | 'settings';

export type TabGuideKey =
  | 'overview-summary'
  | 'overview-comparison'
  | 'index-manual'
  | 'index-schedule'
  | 'index-charts'
  | 'inspect-manual'
  | 'inspect-schedule'
  | 'inspect-charts';

export type GuideBody = {
  title: string;
  /** Short alert under the page header — max ~2 lines. */
  shortAlert?: string;
  paragraphs: string[];
  bullets?: string[];
  /** When true, drawer also shows Google setup video + sections. */
  includeGoogleSetup?: boolean;
};

export const PAGE_GUIDES: Record<PageGuideKey, GuideBody> = {
  overview: {
    title: 'نمای کلی',
    shortAlert:
      'خلاصهٔ وضعیت ایندکس، سهمیهٔ روزانه و روند درخواست‌ها را در یک نگاه ببینید. با انتخاب بازهٔ زمانی، آمار کارت‌ها و نمودارها به‌روز می‌شود.',
    paragraphs: [
      'نمای کلی داشبورد اصلی پروژه است: موجودی URLها، سهمیه‌های Indexing و Inspection، و خلاصهٔ فعالیت اخیر را اینجا می‌بینید.',
      'تب «خلاصه وضعیت» کارت‌های KPI و نمودارهای اصلی را نشان می‌دهد؛ تب «مقایسه» برای دیدن تفاوت‌ها و روندهای پیشرفته‌تر است.',
      'روی کارت‌ها کلیک کنید تا به لیست آدرس‌ها یا لاگ تغییرات با فیلتر مرتبط بروید.',
    ],
    bullets: [
      'بازهٔ زمانی بالای صفحه فقط آمار نمایشی را محدود می‌کند؛ دادهٔ ذخیره‌شده پاک نمی‌شود.',
      'اگر هنوز بازرسی یا ایندکسی انجام نشده، بخشی از کارت‌ها خالی یا صفر می‌ماند.',
    ],
  },
  index: {
    title: 'درخواست ایندکس',
    shortAlert:
      'از اینجا آدرس‌ها را برای ایندکس به گوگل می‌فرستید — دستی یا با زمان‌بندی sitemap. سهمیهٔ روزانهٔ Indexing API را در کارت بالای صفحه ببینید.',
    paragraphs: [
      'این بخش با Google Indexing API کار می‌کند و به گوگل اعلام می‌کند URL تازه‌ای برای خزش آماده است.',
      'ارسال دستی برای کارهای یک‌باره و انبوه است؛ زمان‌بندی برای خواندن دوره‌ای sitemap و ارسال فقط آدرس‌های تازه مناسب است.',
      'نتیجهٔ هر اجرا در تب تاریخچه دیده می‌شود؛ قبل از ارسال، وضعیت HTTP آدرس‌ها را هم می‌توانید بررسی کنید.',
    ],
    bullets: [
      'سقف روزانهٔ گوگل محدود است؛ کارت سهمیه را قبل از ارسال انبوه چک کنید.',
      'URL تکراری در بازهٔ کوتاه ممکن است هشدار بدهد تا سهمیه هدر نرود.',
    ],
  },
  inspect: {
    title: 'وضعیت ایندکس',
    shortAlert:
      'وضعیت ایندکس هر URL را با URL Inspection گوگل بگیرید. برای پایش مداوم از تب زمان‌بندی و اطلاع‌رسانی استفاده کنید.',
    paragraphs: [
      'اینجا همان دادهٔ URL Inspection در Search Console را برای چند یا صدها آدرس به‌صورت دسته‌ای می‌گیرید.',
      'ارسال دستی برای بررسی فوری است؛ زمان‌بندی برای تکرار خودکار و وب‌هوک وقتی وضعیت عوض می‌شود.',
      'نتیجهٔ بازرسی در کاتالوگ «لیست آدرس‌ها» و در صورت تغییر وضعیت در «تغییرات» هم منعکس می‌شود.',
    ],
    bullets: [
      'سهمیهٔ روزانهٔ Inspection جدا از Indexing است؛ کارت بالای صفحه را ببینید.',
      'بدون مالکیت Owner سرویس‌اکانت در Search Console، بازرسی خطا می‌دهد.',
    ],
  },
  urls: {
    title: 'لیست آدرس‌ها',
    shortAlert:
      'کاتالوگ همهٔ URLهای شناخته‌شده با آخرین وضعیت ایندکس. می‌توانید فیلتر کنید، جستجو کنید یا خروجی بگیرید.',
    paragraphs: [
      'هر آدرسی که از sitemap، ارسال ایندکس یا بازرسی وارد سیستم شده در این کاتالوگ می‌ماند.',
      'کارت‌های بالای صفحه تعداد ایندکس‌شده، ایندکس‌نشده، خطا و بدون بازرسی را نشان می‌دهد؛ با کلیک روی کارت فیلتر اعمال می‌شود.',
      'از جزئیات هر ردیف می‌توانید تاریخچهٔ بازرسی همان URL را ببینید یا برای ایندکس/بازرسی بعدی انتخاب کنید.',
    ],
  },
  changes: {
    title: 'تغییرات',
    shortAlert:
      'فقط وقتی بازرسی وضعیت ایندکس را نسبت به بازرسی قبلی عوض کند اینجا ثبت می‌شود. اولین بازرسی هر آدرس در این لاگ نیست.',
    paragraphs: [
      'لاگ تغییرات برای پیگیری بهتر شدن یا بدتر شدن وضعیت ایندکس بین دو بازرسی متوالی است.',
      'می‌توانید بر اساس نوع تغییر، بازهٔ زمانی یا عبارت جستجو فیلتر کنید.',
      'برای دیدن جزئیات بازرسی‌ها به لیست آدرس‌ها یا تاریخچهٔ وضعیت ایندکس بروید.',
    ],
  },
  settings: {
    title: 'تنظیمات',
    shortAlert:
      'کلید Service Account گوگل و در صورت نیاز IndexNow را اینجا مدیریت کنید. بدون Owner در Search Console، ایندکس و بازرسی کار نمی‌کند.',
    paragraphs: [
      'در این صفحه اعتبارنامهٔ گوگل پروژه را آپلود یا عوض می‌کنید و تنظیمات مکمل مثل IndexNow را می‌بینید.',
      'ویدیو و مراحل زیر همان راهنمای راه‌اندازی اولیه است؛ اگر هنوز کلید نساخته‌اید از اینجا هم می‌توانید پیش بروید.',
    ],
    includeGoogleSetup: true,
  },
};

export const TAB_GUIDES: Record<TabGuideKey, GuideBody> = {
  'overview-summary': {
    title: 'خلاصه وضعیت',
    paragraphs: [
      'کارت‌های KPI وضعیت موجودی URL، سهمیه‌های روزانه و فعالیت اخیر را خلاصه می‌کنند.',
      'نمودارها پوشش ایندکس و روند درخواست‌های ایندکس/بازرسی را در بازهٔ انتخاب‌شده نشان می‌دهند.',
      'روی کارت‌ها کلیک کنید تا به صفحهٔ مرتبط با فیلتر مناسب بروید.',
    ],
  },
  'overview-comparison': {
    title: 'مقایسه',
    paragraphs: [
      'این تب برای مقایسهٔ عمیق‌تر روندها و تفاوت وضعیت‌ها در بازهٔ زمانی است.',
      'از نمودارهای پیشرفته برای دیدن جابه‌جایی بین وضعیت‌های ایندکس و الگوی فعالیت استفاده کنید.',
    ],
  },
  'index-manual': {
    title: 'ارسال دستی — درخواست ایندکس',
    paragraphs: [
      'لیست URL یا خروجی sitemap را می‌فرستید تا همین الان درخواست ایندکس در Google Indexing API ثبت شود.',
      'مناسب کارهای انبوه و یک‌باره است؛ پیشرفت و نتیجهٔ درخواست‌ها در تب تاریخچه دیده می‌شود.',
      'قبل از ارسال می‌توانید بررسی HEAD بگیرید تا آدرس‌های خطا را از لیست حذف کنید.',
    ],
  },
  'index-schedule': {
    title: 'زمان‌بندی — درخواست ایندکس',
    paragraphs: [
      'برای یک sitemap زمان‌بندی می‌سازید تا سیستم در بازه‌های منظم آن را بخواند.',
      'معمولاً فقط آدرس‌های تازه‌ای که اخیراً در sitemap آمده‌اند برای ایندکس به گوگل فرستاده می‌شوند.',
      'هدف ایندکس مداوم صفحات جدید بدون ارسال دستی روزانه است؛ اجراها در تب تاریخچه با ستون «نوع اجرا» دیده می‌شوند.',
    ],
  },
  'index-charts': {
    title: 'تاریخچه — درخواست ایندکس',
    paragraphs: [
      'خلاصهٔ فعالیت ارسال به Google Indexing API در همان بازهٔ زمانی کارت‌های آمار بالای صفحه.',
      'جدول تاریخچهٔ درخواست‌ها و جزئیات هر job همین‌جا است.',
    ],
  },
  'inspect-manual': {
    title: 'ارسال دستی — وضعیت ایندکس',
    paragraphs: [
      'URL یا sitemap را برای بازرسی وضعیت ایندکس در Search Console می‌فرستید.',
      'مناسب بررسی انبوه و فوری است؛ نتیجهٔ درخواست‌ها در تب تاریخچه دیده می‌شود.',
    ],
  },
  'inspect-schedule': {
    title: 'زمان‌بندی — وضعیت ایندکس',
    paragraphs: [
      'زمان‌بندی‌های خودکار برای تکرار بازرسی وضعیت ایندکس.',
      'می‌توانید رویداد وب‌هوک تعریف کنید تا وقتی وضعیت عوض شد اطلاع بگیرید.',
      'اجراهای زمان‌بندی در تب تاریخچه با ستون «نوع اجرا» دیده می‌شوند.',
    ],
  },
  'inspect-charts': {
    title: 'تاریخچه — وضعیت ایندکس',
    paragraphs: [
      'وضعیت فعلی کاتالوگ URL بر اساس آخرین بازرسی هر آدرس، به‌اضافهٔ فعالیت بازرسی در بازهٔ انتخاب‌شده.',
      'جدول تاریخچهٔ درخواست‌ها و جزئیات هر جاب همین‌جا است.',
    ],
  },
};

export function getPageGuide(key: PageGuideKey): GuideBody {
  return PAGE_GUIDES[key];
}

export function getTabGuide(key: TabGuideKey): GuideBody {
  return TAB_GUIDES[key];
}

/** Map `/indexer/<page>/…` pathname to the page guide used by the header help icon. */
const INDEXER_PATH_GUIDES: { segment: string; guideKey: PageGuideKey }[] = [
  { segment: '/indexer/overview/', guideKey: 'overview' },
  { segment: '/indexer/index/', guideKey: 'index' },
  { segment: '/indexer/url-inspection/', guideKey: 'inspect' },
  { segment: '/indexer/urls/', guideKey: 'urls' },
  { segment: '/indexer/changes/', guideKey: 'changes' },
  { segment: '/indexer/settings/', guideKey: 'settings' },
];

export function resolveIndexerPageGuide(
  pathname: string,
): GuideBody | null {
  const hit = INDEXER_PATH_GUIDES.find((item) =>
    pathname.includes(item.segment),
  );
  return hit ? getPageGuide(hit.guideKey) : null;
}
